import { loadCSS } from 'helpers/css';
import { DOMHelpers } from 'helpers/dom';
import { FilesHelpers } from 'helpers/files';
import { Size } from 'interfaces/size';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import device from 'managers/device';
import overlayManager from 'managers/overlay';
import { EffectsContainer, withEffects } from 'mixins/withEffects';
import WCCVideo from 'models/attachments/video';
import WCCUpload from 'models/upload';
import { ServicesContext } from 'services/context';

interface AddpipeParams {
    size?: Size
    qualityurl?: string //this is the video res quality settings
    accountHash?: string
    eid?: string //environmentId
    mrt?: number //MRT = Max Record Time (in seconds)
    dup?: 0 | 1 //allow Desktop Upload Prerecorded Video Files
    srec?: 0 | 1//allow screen recording
}

declare const PipeSDK: any;

const $script = require('scriptjs');

const { availableViewportSizeToOverlay, CDN_AWS_ImageURLPrefix, AddPipe_Folder } = settings;

//Lazy addpipe resources loader
//starts loading once called
const resourcesAwaiter = ko.pureComputed(() => {
    return system.getPromiseAwaiter(async () => {
        await Promise.all([
            new Promise(resolve => $script('//cdn.addpipe.com/2.0/pipe.js', resolve)),
            loadCSS('//cdn.addpipe.com/2.0/pipe.css')
        ]);

        return true;
    });
});

const areResourcesLoaded = ko.pureComputed(() => ko.unwrap(resourcesAwaiter()) ?? false);

function getAddPipeSize() {
    const maxWidth = device.width() * availableViewportSizeToOverlay;
    const maxHeight = device.height() * availableViewportSizeToOverlay;

    let width = 640;
    let height = 390;

    if (width > maxWidth) {
        const scale = width / maxWidth;
        width = maxWidth;
        height /= scale;
    } else if (height > maxHeight) {
        const scale = height / maxHeight;
        width /= scale;
        height = maxHeight;
    }

    return { width, height };
}

const addpipeParams = (): AddpipeParams => {
    return {
        size: getAddPipeSize(),
        qualityurl: "avq/720p.xml", //this is the video res quality settings
        accountHash: settings.addPipeAccountHash,
        eid: settings.addPipeEnvironmentId,
        mrt: 600, //MRT = Max Record Time (in seconds)
        dup: 1 //allow Desktop Upload Prerecorded Video Files
    }
}

const videoAddPipeParams = addpipeParams;

const screenRecordingAddPipeParams = () => ({
    ...addpipeParams(),

    mrt: 1800,
    srec: 1//allow screen recording
});

export interface AddPipeManagerConfig {
    preload?: boolean
    container?: SubscribableOrNullableValue<HTMLElement>
}

interface RenderOptions {
    onOpen?: Action
    onClose?: Action
}

interface TemplateData {
    isBusy: Subscribable<boolean>
}

export default class AddPipeManager {
    private ctx: ServicesContext
    private effects = withEffects()
    private container?: SubscribableOrNullableValue<HTMLElement>

    private isCreatingAttachment = ko.observable(false);

    uploads = ko.observableArray<WCCUpload>();
    
    isReady = ko.pureComputed(() => this.checkIfReady())
    isBusy = ko.observable(false);

    /**
     * disposable
     * @param preload will start loading addpipe resources on construction if true
     */
    constructor(preloadOrConfig: AddPipeManagerConfig | boolean = false) {
        this.ctx = this.effects.register(new ServicesContext());

        let preload: boolean

        if (_.isBoolean(preloadOrConfig)) {
            preload = preloadOrConfig;
        } else {
            preload = preloadOrConfig.preload ?? false;
            this.container = preloadOrConfig.container;
        }

        if (preload)
            resourcesAwaiter();
    }

    pickVideo() {
        return this.pick(videoAddPipeParams());
    }

    uploadFile(file: File) {
        return this.pick(videoAddPipeParams(), async (id, recorder) => {
            $(`#${id}`).hide();

            const input = <HTMLInputElement | undefined>await DOMHelpers.waitFor(`#${id} .pipeStartUploading`);

            if (input != undefined) {
                const dt = new DataTransfer();
                dt.items.add(file);

                input.files = dt.files;
                //window.$(input).trigger('change'); //addpipe seems to override $ so we have to call it using reference inside window
                const event = new Event('change');
                input.dispatchEvent(event);
            }
        });
    }

    recordScreen() {
        return this.pick(screenRecordingAddPipeParams(), (id, recorder) => {
            $(`#pipeRecordScreen-${recorder.name}`).click();
        });
    }

    dispose() {
        this.effects.dispose();
    }

    private checkIfReady() {
        return areResourcesLoaded();
    }    

    private async pick(params: StringMap<any>, init?: Action<[id: string, recorder: any]>) {
        if (this.isBusy())
            throw new Error('AddPipe is busy with previous operation');

        this.isBusy(true);

        await this.isReady.when();

        //overlay side effects
        const effects = withEffects();

        //add overlay side effects to component side effects
        //so overlay get disposed on component dispose call
        this.effects.register(effects);

        try {
            return await new Promise<WCCVideo | undefined>(resolve => {
                const id = _.uniqueId('add-pipe-recorder-');
                const tpl = this.getOverlayTemplate(id);
                const container = ko.unwrap(this.container);

                const onOpen = async () => {
                    const openCallback = async () => {
                        //wait for video from addpipe
                        const video = await this.awaitVideo(id, params, effects, recorder => {
                            //remove reconder on overlay dispose
                            effects.register([
                                () => {
                                    try {
                                        recorder.remove();
                                    } catch {
                                    }
                                }
                            ]);

                            init?.(id, recorder);
                        });

                        resolve(video);
                    };
                    
                    let attempts = 0;
                    const checkIfContainerIsRendered = async () => {
                        attempts++;
                        const containerEl = document.getElementById(id);
                        if(containerEl) {
                            openCallback();
                        }
                        else {
                            if(attempts < 10) {
                                setTimeout(checkIfContainerIsRendered, 100);
                            }
                            else {
                                console.error('ADD PIPE CONTAINER NOT FOUND');
                            }
                        }
                    }
                    let containerExists = false;
                    checkIfContainerIsRendered();
                }

                const onClose = () => resolve(undefined)

                if (container != undefined)
                    this.renderInContainer(id, container, tpl, effects, { onOpen, onClose })
                else
                    this.renderInOverlay(tpl, effects, { onOpen, onClose });
            });
        } finally {
            this.isBusy(false);
            effects.dispose();
        }
    }

    private renderInContainer(id: string, container: HTMLElement, tpl: string, effects: EffectsContainer, options?: RenderOptions) {
        const $wrapper = $('<div/>');
        $wrapper.html(tpl);

        const $container = $(container);        
        $container.children().remove();
        $container.append($wrapper);

        const wrapper = $wrapper[0];

        ko.applyBindings(this.getTemplateData(), wrapper);
        ko.utils.domNodeDisposal.addDisposeCallback(wrapper, () => {
            options?.onClose?.()
        });

        const setAddpipeSize = _.once(($addpipe: JQuery) => {
            $addpipe.css({ height: 'auto' });
        });

        const setAddpipeBodySize = _.once(($addpipeBody: JQuery, $addpipeFooter: JQuery) => {
            $addpipeBody.css({ width: 'auto', height: 'auto' });
            $addpipeFooter.css({ width: 'auto' });
        });

        effects.register(() => {
            const observer = new MutationObserver(() => {
                const $addpipe = $(`#${id}`, wrapper);
                const $addpipeBody = $(`#pipeVrec-${id}`, wrapper);
                const $addpipeFooter = $(`#pipeMenu-${id}`, wrapper);

                if ($addpipe.length > 0)
                    setAddpipeSize($addpipe)

                if ($addpipeBody.length > 0 && $addpipeFooter.length > 0) {
                    setAddpipeBodySize($addpipeBody, $addpipeFooter);
                    observer.disconnect();
                }
            });

            observer.observe(wrapper, { childList: true, subtree: true });

            return () => observer.disconnect();
        });

        effects.register([
            () => ko.removeNode(wrapper)
        ]);

        options?.onOpen?.();
    }

    private renderInOverlay(tpl: string, effects: EffectsContainer, options?: RenderOptions) {
        overlayManager.toOverlay(tpl, {
            lock: true,
            buttons: ['close'],

            data: this.getTemplateData(),

            onOpen: stage => {
                effects.register(isCreatingAttachment => {
                    stage.isBusy(isCreatingAttachment);
                }, [this.isCreatingAttachment]);

                //close stage on overlay dispose
                effects.register([
                    () => stage.close()
                ]);

                options?.onOpen?.();
            },

            onClose: () => options?.onClose?.()
        });
    }

    private getTemplateData() {
        return <TemplateData>{
            isBusy: this.isCreatingAttachment
        }
    }

    private awaitVideo(id: string, params: StringMap<any>, effects: EffectsContainer, initRecorder: Action<[any]>) {
        return new Promise<WCCVideo | undefined>((resolve, reject) => {
            const upload = new WCCUpload({ cancellable: false });

            PipeSDK.insert(id, params, (recorder: any) => {
                recorder.onDesktopVideoUploadStarted = async (recorderId: string, filename: string, filetype: string, audioOnly: boolean) => {
                    if (recorderId === id) {
                        upload.name(filename);
                        this.uploads.push(upload);

                        const fileInput = <HTMLInputElement | undefined>document.getElementById(`pipeStartUploading-${recorder.name}`);

                        if (fileInput != undefined) {
                            const file = fileInput.files?.[0];

                            if (file != undefined) {
                                const preview = await FilesHelpers.getPreview(file);

                                upload.width(preview.width);
                                upload.height(preview.height);
                                upload.preview(preview.src);
                            }
                        }
                    }
                }

                recorder.onDesktopVideoUploadProgress = (recorderId: string, percent: number) => {
                    if (recorderId === id)
                        upload.progress(percent);
                }

                //this event gets fired on selected file upload
                recorder.onDesktopVideoUploadSuccess = (recorderId: string, filename: string, filetype: string, videoId: string, audioOnly: boolean, location: string) => {
                    if (recorderId === id) {
                        this.uploads.remove(upload);
                        resolve(this.createAttachment(location, filename, videoId));
                    }
                }

                recorder.onDesktopVideoUploadFailed = (recorderId: string, error: string) => {
                    if (recorderId === id) {
                        this.uploads.remove(upload);
                        reject(new Error(error));
                    }
                }

                recorder.onVideoUploadStarted = async (recorderId: string, filename: string, filetype: string, audioOnly: boolean) => {
                    if (recorderId === id) {
                        upload.name(filename);
                        this.uploads.push(upload);

                        const fileInput = <HTMLInputElement | undefined>document.querySelector(`#${id} .pipeStartUploading`);

                        if (fileInput != undefined) {
                            const file = fileInput.files?.[0];

                            if (file != undefined) {
                                const preview = await FilesHelpers.getPreview(file);

                                upload.width(preview.width);
                                upload.height(preview.height);
                                upload.preview(preview.src);
                            }
                        }
                    }
                }

                recorder.onVideoUploadProgress = (recorderId: string, percent: number) => {
                    if (recorderId === id)
                        upload.progress(percent);
                }

                //this event gets fired for mobile upload success - might be needed for direct file uploads on mobile.
                recorder.onVideoUploadSuccess = (recorderId: string, filename: string, filetype: string, videoId: string, audioOnly: boolean, location: string) => {
                    if (recorderId === id) {
                        this.uploads.remove(upload);
                        resolve(this.createAttachment(location, filename, videoId));
                    }
                }

                recorder.onVideoUploadFailed = (recorderId: string, error: string) => {
                    if (recorderId === id) {
                        this.uploads.remove(upload);
                        reject(new Error(error));
                    }
                }

                recorder.onSaveOk = (recorderId: string, streamName: string, streamDuration: number, cameraName: string, micName: string, userId: string, audioCodec: string, videoCodec: string, videoId: string, audioOnly: boolean, location: string) => {
                    if (recorderId === id)
                        resolve(this.createAttachment(location, streamName, videoId));
                }

                recorder.onConnectionClosed = (recorderId: string) => {
                    if (recorderId === id) {
                        system.handleError(messages.UnknownError);
                        reject(new Error('Connection is lost'));
                    }
                }

                initRecorder(recorder);                
            });
        });
    }

    private async createAttachment(location: string, streamName: string, videoId?: string) {
        this.isCreatingAttachment(true);

        try {
            const thumbnailName = `${streamName}.jpg`;
            const thumbnailURL = `${CDN_AWS_ImageURLPrefix}/${AddPipe_Folder}/${thumbnailName}`;

            const videoName = `${streamName}.mp4`;
            const videoURL = `${CDN_AWS_ImageURLPrefix}/${AddPipe_Folder}/${videoName}`;

            const [thumbnailResult, videoResult] = await Promise.all([
                this.ctx.userFilesService.queries.addpipeFiles().add({ Link: thumbnailURL, Name: thumbnailName }),
                this.ctx.userFilesService.queries.addpipeFiles().add({ Link: videoURL, Name: videoName, AddPipeId: videoId })
            ]);

            const attachment = new WCCVideo({
                FileType: enums.AttachmentTypes.Video.value,
                CreateDate: new Date().toISOString(),

                Files: [
                    {
                        UserFileId: thumbnailResult.FileId,
                        Name: thumbnailName,
                        Extension: 'jpg',
                        Link: thumbnailResult.SignedS3Link, //thumbnailURL,
                        Type: enums.UserFileTypes.PendingVideoThumbnail.value,
                        CreateDate: new Date().toISOString()
                    },

                    {
                        UserFileId: videoResult.FileId,
                        Name: videoName,
                        Extension: 'mp4',
                        Link: videoResult.SignedS3Link,//videoURL,
                        Type: enums.UserFileTypes.PendingVideo.value,
                        CreateDate: new Date().toISOString()
                    },
                ]
            });

            return attachment;
        } finally {
            this.isCreatingAttachment(false);
        }
    }

    private getOverlayTemplate(id: string) {
        return `
<div>
    <div data-bind="visible: !isBusy()">
        <div id="${id}"></div>
    </div>
    <spinner-new params="active: isBusy, height: 100, color: '#fff'"></spinner-new>
</div>`;
    }
}